import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import "@/i18n";

import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { FavoritesProvider } from "@/lib/favorites";
import CookieBanner from "@/components/CookieBanner";

import LoginPage from "@/pages/Login";
import LandingPage from "@/pages/Landing";
import HomePage from "@/pages/Home";
import MarketplacePage from "@/pages/Marketplace";
import VehicleDetail from "@/pages/VehicleDetail";
import DealerDetail from "@/pages/DealerDetail";
import AICopilotPage from "@/pages/AICopilot";
import PratichePage from "@/pages/Pratiche";
import PraticaDetail from "@/pages/PraticaDetail";
import TariffarioPage from "@/pages/Tariffario";
import PrivatiPage from "@/pages/Privati";
import PrivatiAutoPage from "@/pages/PrivatiAuto";
import SecureTradePage from "@/pages/SecureTrade";
import ProfilePage from "@/pages/Profile";
import ServicesPage from "@/pages/Services";
import MyStockPage from "@/pages/MyStock";
import AdminApp from "@/pages/Admin";
import StrumentiPage from "@/pages/Strumenti";
import ImpressumPage from "@/pages/Impressum";
import PrivacyPage from "@/pages/Privacy";
import ContattiPage from "@/pages/Contatti";
import VerifyEmailPage from "@/pages/VerifyEmail";
import EmailVerificationGate from "@/components/EmailVerificationGate";
import { useAuth } from "@/context/AuthContext";

/**
 * Sequential gate for authenticated routes: EMAIL VERIFICATION first,
 * then the existing dealer-association gate. Admins bypass. Kept inline
 * (no extra file) because the logic is minimal and coupled to the
 * router layout.
 */
function EmailVerifiedGate({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  const isAdmin = (user.role || "").toLowerCase() === "admin";
  if (!isAdmin && user.email_verified === false) {
    return <EmailVerificationGate />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <FavoritesProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes (no auth) */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verifica-email" element={<VerifyEmailPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contatti" element={<ContattiPage />} />
            <Route path="/contact" element={<Navigate to="/contatti" replace />} />
            <Route path="/privati" element={<PrivatiPage />} />
            <Route path="/privati/auto" element={<PrivatiAutoPage />} />

            <Route
              element={
                <ProtectedRoute>
                  <EmailVerifiedGate>
                    <Layout />
                  </EmailVerifiedGate>
                </ProtectedRoute>
              }
            >
              <Route path="/home" element={<HomePage />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/vehicle/:id" element={<VehicleDetail />} />
              <Route path="/vehicles/:id" element={<VehicleDetail />} />
              <Route path="/dealers/:id" element={<DealerDetail />} />
              <Route path="/ai" element={<AICopilotPage />} />
              <Route path="/pratiche" element={<PratichePage />} />
              <Route path="/pratiche/:id" element={<PraticaDetail />} />
              <Route path="/tariffario" element={<TariffarioPage />} />
              <Route path="/secure-trade" element={<SecureTradePage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/le-mie-auto" element={<MyStockPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/strumenti" element={<StrumentiPage />} />
              <Route path="/admin/*" element={<AdminApp />} />
              {/* Legacy redirects — Packages retired in R1 */}
              <Route path="/packages" element={<Navigate to="/marketplace" replace />} />
              <Route path="/packages/*" element={<Navigate to="/marketplace" replace />} />
              <Route path="/orders" element={<Navigate to="/pratiche" replace />} />
              <Route path="/orders/*" element={<Navigate to="/pratiche" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
          <CookieBanner />
        </BrowserRouter>
      </FavoritesProvider>
    </AuthProvider>
  );
}
